/*
 * ------------ Theme Common JS ------------
 */

if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
  cartError: "cart-error",
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

// Holds auto-moving content still. Keyboard focus anywhere inside `root` always
// holds it; the pointer only when a `hoverTarget` is passed (the component's
// pause-on-hover setting). Other reasons (offscreen, a modal) go through
// set(reason, active). onChange fires only when the held state flips.
function createMotionHold(
  root,
  { hoverTarget = null, onChange, onFocusIn, onFocusOut } = {}
) {
  const reasons = new Set();
  const controller = new AbortController();
  const options = { signal: controller.signal };
  // A click or tap focuses too, and stopping on every tap reads as a freeze.
  const isKeyboardFocus = (el) => !!el?.matches?.(":focus-visible");

  const set = (reason, active) => {
    const wasHeld = reasons.size > 0;
    if (active) reasons.add(reason);
    else reasons.delete(reason);
    if (wasHeld !== reasons.size > 0) onChange?.(reasons.size > 0);
  };

  const leave = () => {
    set("focus", false);
    onFocusOut?.();
  };

  root.addEventListener(
    "focusin",
    (event) => {
      if (!isKeyboardFocus(event.target)) return;
      set("focus", true);
      onFocusIn?.(event.target);
    },
    options
  );
  root.addEventListener(
    "focusout",
    (event) => {
      if (!root.contains(event.relatedTarget)) leave();
    },
    options
  );
  // Focus can also vanish without a focusout (its element removed or inert).
  document.addEventListener(
    "focusin",
    (event) => {
      if (reasons.has("focus") && !root.contains(event.target)) leave();
    },
    options
  );

  if (hoverTarget && window.matchMedia("(hover: hover)").matches) {
    const hover = (active) => (event) => {
      if (event.pointerType !== "touch") set("hover", active);
    };
    hoverTarget.addEventListener("pointerenter", hover(true), options);
    hoverTarget.addEventListener("pointerleave", hover(false), options);
    if (hoverTarget.matches(":hover")) set("hover", true);
  }

  // A rebuild (editor reload, resize, tab switch) can land with focus inside.
  const focused = document.activeElement;
  if (root.contains(focused) && isKeyboardFocus(focused)) set("focus", true);

  return {
    get held() {
      return reasons.size > 0;
    },
    set,
    destroy() {
      controller.abort();
      reasons.clear();
    },
  };
}

// A drift (no delay, one long linear step) would keep moving until its step
// ends, so a hold stops it where it is; the release finishes the cut step at
// the same pace and then starts autoplay again.
function createSwiperDriftFreeze(swiper) {
  const autoplay = swiper.autoplay;
  let frozen = false;
  let finishing = false;
  let ownStop = false;
  let stepEndsAt = 0;
  let remaining = 0;

  const stopAutoplay = () => {
    ownStop = true;
    autoplay.stop();
    ownStop = false;
  };
  const onStepStart = (s, speed) => {
    stepEndsAt = performance.now() + speed;
  };
  const onStepEnd = () => {
    if (!finishing) return;
    finishing = false;
    autoplay.start();
  };
  // Stopped from outside (e.g. below the drift breakpoint): stay stopped.
  const onStop = () => {
    if (ownStop) return;
    frozen = false;
    finishing = false;
  };

  swiper.on("beforeTransitionStart", onStepStart);
  swiper.on("transitionEnd", onStepEnd);
  swiper.on("autoplayStop", onStop);

  return {
    hold() {
      if (!autoplay.running && !frozen && !finishing) return;
      if (autoplay.running) stopAutoplay();
      finishing = false;
      if (frozen) return;

      frozen = true;
      remaining = 0;
      if (!swiper.animating) return;

      remaining = Math.max(stepEndsAt - performance.now(), 0);
      const translate = swiper.getTranslate();
      swiper.setTransition(0);
      swiper.setTranslate(translate);
      // Close the cut step the way its own transitionend would, so the arrows
      // (a looping slider ignores them mid-step) respond while it is held.
      swiper.wrapperEl.dispatchEvent(new Event("transitionend"));
    },
    release() {
      if (!frozen) return;
      frozen = false;
      finishing = true;
      // A step already under way (arrows, swipe) ends on its own.
      if (swiper.animating) return;
      if (!swiper.slideTo(swiper.activeIndex, remaining, false)) onStepEnd();
    },
    destroy() {
      swiper.off("beforeTransitionStart", onStepStart);
      swiper.off("transitionEnd", onStepEnd);
      swiper.off("autoplayStop", onStop);
    },
  };
}

// Drives a Swiper autoplay from createMotionHold. Swiper resumes on its own
// after every transition, on a tab return and on start(), so the hold is
// re-applied each time it does. Its native pauseOnMouseEnter must stay off:
// its pointerleave would resume while focus is still inside.
function holdSwiperAutoplay(
  swiper,
  root,
  { hoverTarget = null, freeze = false } = {}
) {
  const autoplay = swiper?.autoplay;
  if (!autoplay || !root) return null;

  const drift = freeze ? createSwiperDriftFreeze(swiper) : null;
  const apply = (held) => {
    if (swiper.destroyed) return;
    if (drift) {
      if (held) drift.hold();
      else drift.release();
      return;
    }
    // A second pause() eats the remaining delay, so only act on a real change.
    if (!autoplay.running) return;
    if (held && !autoplay.paused) autoplay.pause();
    else if (!held && autoplay.paused) autoplay.resume();
  };

  const hold = createMotionHold(root, { hoverTarget, onChange: apply });
  const reassert = () => {
    if (hold.held) apply(true);
  };
  swiper.on("autoplayResume autoplayStart", reassert);

  return {
    get held() {
      return hold.held;
    },
    set: hold.set,
    destroy() {
      swiper.off("autoplayResume autoplayStart", reassert);
      drift?.destroy();
      hold.destroy();
    },
  };
}

function followSwiperFocus(sliderEl, { signal } = {}) {
  sliderEl.addEventListener(
    "focusin",
    (event) => {
      const swiper = sliderEl.swiper;
      const target = event.target;
      if (!swiper || swiper.destroyed || !target.matches(":focus-visible")) {
        return;
      }
      const slideEl = swiper.slides.find((slide) => slide.contains(target));
      if (!slideEl) return;

      requestAnimationFrame(() => {
        if (swiper.destroyed || !slideEl.isConnected) return;
        sliderEl.scrollLeft = 0;
        const { translate } = swiper;
        const start = swiper.rtlTranslate ? translate : -translate;
        const before = slideEl.swiperSlideOffset - start;
        const size = slideEl.swiperSlideSize;
        if (before >= -1 && before + size <= swiper.size + 1) return;

        swiper.slideTo(swiper.slides.indexOf(slideEl), 0);
      });
    },
    { signal }
  );
}

// Shared initialization script initSectionSliderLayout is used for sliders across sections (Popular products, Featured blog, Collection list, Multicolumn, etc.). A section renders more than one slider when it has tab pages (see initCardTabs) — each page's slider is initialized independently.
function initSectionSliderLayout(section) {
  if (!section.closest("[data-section-has-card-slider='true']")) return;

  section.querySelectorAll(".js-section-card-slider").forEach((sliderEl) => {
    initOneCardSlider(section, sliderEl);
  });
}

function initOneCardSlider(section, sliderEl) {
  const controlsScope = sliderEl.closest(".section-card-container") || section;

  // Only pick controls that belong to THIS slider, not the nav/arrows that
  // product cards render inside their own media slider (which live inside sliderEl).
  const findControl = (selector) => {
    const matches = controlsScope.querySelectorAll(selector);
    for (let i = 0; i < matches.length; i++) {
      if (!sliderEl.contains(matches[i])) return matches[i];
    }
    return null;
  };

  const paginationEl = findControl(".slider-pagination");
  const nextBtn = findControl(".slider-nav-button--next");
  const prevBtn = findControl(".slider-nav-button--prev");
  const hasNavigation = sliderEl.getAttribute("data-navigation") === "true";
  const hasPagination = sliderEl.getAttribute("data-pagination") === "true";

  const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
  const hasSliderAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
  const pauseAutoplayOnHover =
    sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
  const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 8000);
  const slidesPerRow = Number(sliderEl.dataset.slidesPerRow || 4);
  const slidesPerRowMobile = Number(sliderEl.dataset.slidesPerRowMobile || 1);

  const slidesPerRow576 = slidesPerRow > 1 ? 2 : 1;
  const slidesPerRow990 =
    slidesPerRow > 2 ? slidesPerRow576 + 1 : slidesPerRow576;
  const slidesPerRow1200 = slidesPerRow >= 4 ? 4 : slidesPerRow;

  // Loop needs slides to spare: with nothing overflowing there is no wrap to
  // make, and Swiper locks the slider anyway. Count only this slider's own
  // slides — a product card's media slider nests its own. The verdict is taken
  // against the CURRENT viewport, not the widest one: a set too short to wrap
  // four-up on desktop still wraps fine one-up on a phone.
  const trackEl = sliderEl.querySelector(".swiper-wrapper");
  const slideCount = trackEl
    ? Array.from(trackEl.children).filter(
        (el) =>
          el.classList.contains("swiper-slide") &&
          !el.classList.contains("section-card-item--tab-hidden")
      ).length
    : 0;
  const slidesPerViewAt = (width) => {
    if (width >= 1200) return slidesPerRow1200;
    if (width >= 990) return slidesPerRow990;
    if (width >= 576) return slidesPerRow576;
    return slidesPerRowMobile;
  };
  const canLoopAt = (width) => slideCount > slidesPerViewAt(width);
  const canLoop = canLoopAt(window.innerWidth);
  const isLooping = sliderLoop && canLoop;

  // A page that doesn't divide the set leaves Swiper's loop dead-ended part-way
  // along the track (its own "not even to slidesPerGroup" case), so a looping
  // slider that cannot page by whole rows pages by a single card instead.
  const slidesPerGroupFor = (perView) => {
    if (!isLooping || slideCount % perView === 0) return perView;
    return 1;
  };

  // Take spaceBetween from the track's --card-gap so the running slider lands on
  // the same gap the pre-init CSS already drew, whatever unit it is authored in.
  // Measured rather than parsed: a custom property resolves no units on its own.
  const spaceBetween = (() => {
    if (!trackEl) return 1;
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:absolute;visibility:hidden;width:var(--card-gap);";
    trackEl.appendChild(probe);
    const gap = probe.getBoundingClientRect().width;
    probe.remove();
    return gap || 1;
  })();

  const sliderSettings = {
    loop: isLooping,
    // Swiper otherwise pads the last group with blank slides so the count
    // divides by slidesPerGroup — with an odd cards-per-row that shows up as
    // empty cells between the last and the first slide.
    loopAddBlankSlides: false,
    watchOverflow: true,
    slidesPerView: slidesPerRowMobile,
    slidesPerGroup: slidesPerGroupFor(slidesPerRowMobile),
    spaceBetween,
    speed: 800,
    watchSlideProgress: true,
    allowTouchMove: true,
    mousewheel: {
      forceToAxis: true,
    },
    a11y: {
      scrollOnFocus: false,
    },
    autoplay: hasSliderAutoplay
      ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
        }
      : false,
    pagination: hasPagination
      ? {
          el: paginationEl,
          clickable: true,
        }
      : false,
    navigation: hasNavigation
      ? {
          nextEl: nextBtn,
          prevEl: prevBtn,
          disabledClass: "swiper-button-disabled",
        }
      : false,
    breakpoints: {
      576: {
        slidesPerView: slidesPerRow576,
        slidesPerGroup: slidesPerGroupFor(slidesPerRow576),
        spaceBetween,
      },
      990: {
        slidesPerView: slidesPerRow990,
        slidesPerGroup: slidesPerGroupFor(slidesPerRow990),
        spaceBetween,
      },
      1200: {
        slidesPerView: slidesPerRow1200,
        slidesPerGroup: slidesPerGroupFor(slidesPerRow1200),
        spaceBetween,
      },
    },
    on: {
      autoplayTimeLeft(s, time, progress) {
        if (hasPagination && paginationEl) {
          paginationEl.style.setProperty("--slider-progress", 1 - progress);
        }
      },
    },
  };

  // Tear down the prior Swiper + observer before re-init (theme editor re-runs this).
  if (sliderEl._cardSliderObserver) {
    sliderEl._cardSliderObserver.disconnect();
    sliderEl._cardSliderObserver = null;
  }
  if (sliderEl._cardSliderHold) {
    sliderEl._cardSliderHold.destroy();
    sliderEl._cardSliderHold = null;
  }
  if (sliderEl._cardSliderLoopWatcher) {
    window.removeEventListener("resize", sliderEl._cardSliderLoopWatcher);
    sliderEl._cardSliderLoopWatcher = null;
  }
  if (sliderEl._cardSliderKeys) {
    sliderEl._cardSliderKeys.abort();
    sliderEl._cardSliderKeys = null;
  }
  if (sliderEl.swiper) {
    sliderEl.swiper.destroy(true, true);
  }

  new Swiper(sliderEl, sliderSettings);

  const keysController = new AbortController();
  sliderEl._cardSliderKeys = keysController;

  followSwiperFocus(sliderEl, { signal: keysController.signal });

  // Swiper's own key handler for the bullets slides by raw index, which lands on
  // the wrong card once the slider loops. Forwarding the key to a real click
  // takes the same path as the pointer, and Space starts working with it.
  if (hasPagination && paginationEl) {
    paginationEl.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        const bullet = event.target.closest(".swiper-pagination-bullet");
        if (!bullet) return;

        event.preventDefault();
        event.stopPropagation();
        bullet.click();
      },
      { signal: keysController.signal, capture: true }
    );
  }

  // `loop` is one of the params Swiper cannot take from a breakpoint, so a
  // viewport change that flips the verdict above has to rebuild the slider.
  if (sliderLoop) {
    const loopWatcher = () => {
      if (canLoopAt(window.innerWidth) !== canLoop) {
        initOneCardSlider(section, sliderEl);
      }
    };

    sliderEl._cardSliderLoopWatcher = loopWatcher;
    window.addEventListener("resize", loopWatcher);
  }

  if (hasSliderAutoplay && sliderEl.swiper) {
    // The controls scope carries the arrows and bullets, so their focus holds too.
    sliderEl._cardSliderHold = holdSwiperAutoplay(
      sliderEl.swiper,
      controlsScope,
      { hoverTarget: pauseAutoplayOnHover ? sliderEl : null }
    );

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        sliderEl._cardSliderHold?.set("offscreen", !entry.isIntersecting);
      });
    });

    sectionObserver.observe(section);
    sliderEl._cardSliderObserver = sectionObserver;
  }
}

// Sections with tabs (see initCardTabs below) reorganize their DOM before
// the slider can be initialized correctly, so they call initSectionSliderLayout
// themselves once that's done — skip them here to avoid initializing twice
// in the wrong order (which would leave Swiper holding stale slide elements).
document.addEventListener("DOMContentLoaded", () => {
  const sectionsWithCardSlider = document.querySelectorAll(
    "[data-section-has-card-slider='true']"
  );
  sectionsWithCardSlider.forEach((section) => {
    if (section.hasAttribute("data-section-has-card-tabs")) return;
    initSectionSliderLayout(section);
  });
});

document.addEventListener("shopify:section:load", function (event) {
  if (typeof event.target?.querySelector !== "function") return;

  const section = event.target?.querySelector(
    "[data-section-has-card-slider='true']"
  );
  if (section && !section.hasAttribute("data-section-has-card-tabs")) {
    initSectionSliderLayout(section);
  }
});

document.addEventListener("shopify:section:unload", function (event) {
  if (typeof event.target?.querySelectorAll !== "function") return;

  event.target
    .querySelectorAll(".js-section-card-slider")
    .forEach((sliderEl) => {
      if (sliderEl._cardSliderObserver) {
        sliderEl._cardSliderObserver.disconnect();
        sliderEl._cardSliderObserver = null;
      }
      if (sliderEl._cardSliderHold) {
        sliderEl._cardSliderHold.destroy();
        sliderEl._cardSliderHold = null;
      }
      if (sliderEl._cardSliderLoopWatcher) {
        window.removeEventListener("resize", sliderEl._cardSliderLoopWatcher);
        sliderEl._cardSliderLoopWatcher = null;
      }
      if (sliderEl._cardSliderKeys) {
        sliderEl._cardSliderKeys.abort();
        sliderEl._cardSliderKeys = null;
      }
      if (sliderEl.swiper) {
        sliderEl.swiper.destroy(true, true);
      }
    });
});

// Hidden by class, never by an inline style: rebuilding the slider destroys the
// Swiper with cleanStyles, which would wipe an inline display and bring every
// other tab's cards back.
const TAB_HIDDEN_CLASS = "section-card-item--tab-hidden";
const TAB_FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

function setCardFocusability(card, isActive) {
  card.querySelectorAll(TAB_FOCUSABLE).forEach((el) => {
    if (isActive) {
      el.removeAttribute("tabindex");
    } else {
      el.setAttribute("tabindex", "-1");
    }
  });
}

function buildPlainTabs(items) {
  return (tabHandle) => {
    items.forEach((item) => {
      const targets = (item.dataset.tabTarget || "").split(",");
      item.classList.toggle(TAB_HIDDEN_CLASS, !targets.includes(tabHandle));
    });
  };
}

function buildSlidingTabs(container, wrapper, tabOrder, rawItems) {
  const cellClassName = rawItems[0].className;

  const bySlot = new Map();
  rawItems.forEach((item) => {
    const slot = Number(item.dataset.slotIndex);
    if (!bySlot.has(slot)) bySlot.set(slot, new Map());
    bySlot.get(slot).set(item.dataset.tabTarget, item);
  });

  const maxSlot = Math.max(...bySlot.keys());
  const fragment = document.createDocumentFragment();
  const cells = [];

  for (let slot = 0; slot <= maxSlot; slot++) {
    const group = bySlot.get(slot) || new Map();

    const cell = document.createElement("div");
    cell.className = cellClassName;

    const track = document.createElement("div");
    track.className = "section-card-item__track";

    const supportedTabs = [];
    tabOrder.forEach((tab) => {
      const original = group.get(tab);
      if (original) {
        original.removeAttribute("style");
        original.removeAttribute("data-tab-target");
        original.removeAttribute("data-slot-index");
        original.className = "section-card-item__card";
        track.appendChild(original);
        supportedTabs.push(tab);
      } else {
        const empty = document.createElement("div");
        empty.className =
          "section-card-item__card section-card-item__card--empty";
        track.appendChild(empty);
      }
    });

    cell.dataset.tabs = supportedTabs.join(",");
    cell.appendChild(track);
    fragment.appendChild(cell);
    cells.push(cell);
  }

  wrapper.replaceChildren(fragment);

  const tabIndexOf = (tabHandle) => tabOrder.indexOf(tabHandle);

  return (tabHandle, index) => {
    container.style.setProperty("--active-tab-index", index);
    const activeIndex = index >= 0 ? index : tabIndexOf(tabHandle);

    cells.forEach((cell) => {
      const supported = cell.dataset.tabs ? cell.dataset.tabs.split(",") : [];
      cell.classList.toggle(TAB_HIDDEN_CLASS, !supported.includes(tabHandle));

      // The whole stack of tab variants stays in the DOM and only slides out of
      // view, so the off-screen cards have to leave the tab order too.
      Array.from(cell.querySelectorAll(".section-card-item__card")).forEach(
        (card, cardIndex) => {
          const isActive = cardIndex === activeIndex;
          card.setAttribute("aria-hidden", isActive ? "false" : "true");
          setCardFocusability(card, isActive);
        }
      );
    });
  };
}

function initCardTabs(container) {
  const wrapper = container.querySelector(".section-card-wrapper");
  const tabs = Array.from(
    container.querySelectorAll("button[data-filter-target]")
  );
  if (!wrapper || tabs.length === 0) return;

  const tabOrder = tabs.map((tab) => tab.dataset.filterTarget);
  const rawItems = Array.from(wrapper.children).filter(
    (el) => el.dataset.tabTarget !== undefined
  );
  if (rawItems.length === 0) return;

  const setActiveTab =
    container.dataset.cardTabsCreative === "true"
      ? buildSlidingTabs(container, wrapper, tabOrder, rawItems)
      : buildPlainTabs(rawItems);

  const initialTab =
    tabs.find((tab) => tab.classList.contains("active")) || tabs[0];
  setActiveTab(
    initialTab.dataset.filterTarget,
    tabOrder.indexOf(initialTab.dataset.filterTarget)
  );

  const controller = new AbortController();
  tabs.forEach((tab) => {
    tab.addEventListener(
      "click",
      () => {
        if (tab.classList.contains("active")) return;
        tabs.forEach((item) => item.classList.remove("active"));
        tab.classList.add("active");
        setActiveTab(
          tab.dataset.filterTarget,
          tabOrder.indexOf(tab.dataset.filterTarget)
        );

        // The visible set decides loop, slidesPerGroup, the bullet count and
        // whether the slider locks at all, and none of those can be updated in
        // place — the tab switch rebuilds the slider instead.
        if (container.querySelector(".js-section-card-slider")) {
          initSectionSliderLayout(container);
        }
      },
      { signal: controller.signal }
    );
  });

  container._cardTabsCleanup = () => controller.abort();

  initSectionSliderLayout(container);
}

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll("[data-section-has-card-tabs='true']")
    .forEach(initCardTabs);
});

document.addEventListener("shopify:section:load", function (event) {
  if (typeof event.target?.querySelector !== "function") return;

  const container = event.target?.querySelector(
    "[data-section-has-card-tabs='true']"
  );
  if (container) initCardTabs(container);
});

document.addEventListener("shopify:section:unload", function (event) {
  if (typeof event.target?.querySelectorAll !== "function") return;

  event.target
    .querySelectorAll("[data-section-has-card-tabs='true']")
    .forEach((container) => {
      if (container._cardTabsCleanup) {
        container._cardTabsCleanup();
        container._cardTabsCleanup = null;
      }
    });
});

// Vertical follow for a content group inside a full-height media card
// (banner-grid, lookbook): tracks the pointer on desktop, tracks the viewport
// center as the tall card scrolls by on mobile — unless [data-cursor-follow-
// desktop-only] is set, in which case mobile stays static. Both paths clamp
// the offset so the content never leaves the card (or slides under the
// header, when this card sits right below it). The pointer path eases via a
// per-frame lerp on a RAF loop so nested :hover (a revealed button) never
// restarts it; the scroll path applies directly since easing would make it
// lag behind the user's own scroll motion.
function initCursorFollow(container) {
  if (!container || container._cursorFollowCleanup) return;

  const target = container.querySelector("[data-cursor-follow-target]");
  if (!target) return;

  // Some sections (banner-grid) want the follow effect on desktop only —
  // mobile stays fully static there, instead of tracking scroll position.
  const desktopOnly = container.hasAttribute("data-cursor-follow-desktop-only");

  const desktopQuery = window.matchMedia(
    "(min-width: 750px) and (hover: hover)"
  );
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const EASE = 0.12; // fraction of the remaining distance closed per frame

  let targetY = 0;
  let currentY = 0;
  let rafId = null;
  let scrollRafId = null;

  const clampToCard = (raw, rect) => {
    const maxOffset = Math.max(0, rect.height / 2 - target.offsetHeight / 2);
    const clamped = Math.max(-maxOffset, Math.min(maxOffset, raw));
    // Never let the content's top edge rise above the header (relevant when
    // this card is the first section, pinned under a transparent header).
    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height"
        )
      ) || 0;
    const neutralTop = rect.top + rect.height / 2 - target.offsetHeight / 2;
    const minY = Math.max(-maxOffset, headerHeight - neutralTop);
    return Math.max(minY, clamped);
  };

  const apply = () => {
    target.style.setProperty("--cursor-follow-y", `${currentY.toFixed(2)}px`);
  };

  const tick = () => {
    currentY += (targetY - currentY) * EASE;
    if (Math.abs(targetY - currentY) < 0.1) {
      // settle exactly on target, then let the loop go idle
      currentY = targetY;
      apply();
      rafId = null;
      return;
    }
    apply();
    rafId = requestAnimationFrame(tick);
  };

  const startLoop = () => {
    if (rafId === null) rafId = requestAnimationFrame(tick);
  };

  const onMove = (event) => {
    if (!desktopQuery.matches || reducedMotion.matches) {
      targetY = 0;
      startLoop();
      return;
    }
    const rect = container.getBoundingClientRect();
    if (rect.height === 0) return;
    // Track the pointer across the FULL card height (top→bottom), clamped so the
    // content stays inside the card edges — matches the reference sites' feel.
    const raw = event.clientY - (rect.top + rect.height / 2);
    targetY = clampToCard(raw, rect);
    startLoop();
  };

  const onLeave = () => {
    if (container.classList.contains("is-modal-open")) return;
    targetY = 0;
    startLoop();
  };

  const updateScroll = () => {
    scrollRafId = null;
    if (desktopOnly || desktopQuery.matches || reducedMotion.matches) return;
    const rect = container.getBoundingClientRect();
    if (rect.height === 0) return;
    const raw = window.innerHeight / 2 - (rect.top + rect.height / 2);
    currentY = clampToCard(raw, rect);
    targetY = currentY;
    apply();
  };

  const onScroll = () => {
    if (scrollRafId === null) scrollRafId = requestAnimationFrame(updateScroll);
  };

  container.addEventListener("pointermove", onMove);
  container.addEventListener("pointerleave", onLeave);
  if (!desktopOnly) {
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateScroll();
  }

  container._cursorFollowCleanup = () => {
    container.removeEventListener("pointermove", onMove);
    container.removeEventListener("pointerleave", onLeave);
    if (!desktopOnly) {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    }
    if (rafId !== null) cancelAnimationFrame(rafId);
    if (scrollRafId !== null) cancelAnimationFrame(scrollRafId);
    rafId = null;
    scrollRafId = null;
    targetY = 0;
    currentY = 0;
    apply();
    container._cursorFollowCleanup = null;
  };
}

function teardownCursorFollow(root) {
  if (typeof root?.querySelectorAll !== "function") return;
  root.querySelectorAll("[data-cursor-follow]").forEach((container) => {
    if (container._cursorFollowCleanup) container._cursorFollowCleanup();
  });
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-cursor-follow]").forEach(initCursorFollow);
});

document.addEventListener("shopify:section:load", (event) => {
  if (typeof event.target?.querySelectorAll !== "function") return;
  event.target
    .querySelectorAll("[data-cursor-follow]")
    .forEach(initCursorFollow);
});

document.addEventListener("shopify:section:unload", (event) => {
  teardownCursorFollow(event.target);
});

if (navigator.userAgent.indexOf("iPhone") > -1) {
  document
    .querySelector("[name=viewport]")
    .setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1"
    );
}

function lockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.documentElement.style.setProperty(
      "--current-body-scroll",
      `${-1 * openScrollY}px`
    );
    document.documentElement.style.setProperty("scroll-behavior", "unset");
    document.body.classList.add("forced-scroll-locked");
  }
}

function unlockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.body.classList.remove("forced-scroll-locked");
    document.documentElement.scrollTop = openScrollY;
    document.documentElement.style.removeProperty("--current-body-scroll");
    document.documentElement.style.removeProperty("scroll-behavior");
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter(
    (element) => element.offsetParent !== null && !element.closest("[inert]")
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", "false");

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-mobile-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

// Drawers and modals move focus with a script call, and WebKit reports a script focus as
// :focus-visible — so a tap leaves the keyboard ring on the trigger. Focus still moves for
// assistive tech; only the ring is held back while the last input came from a pointer.
let lastInputWasKeyboard = false;

document.addEventListener(
  "keydown",
  (event) => {
    lastInputWasKeyboard = true;
    // A key press on the element a tap focused brings its ring back — not a bare modifier or
    // a shortcut chord, which say nothing about keyboard navigation
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      ["Shift", "Meta", "Control", "Alt", "CapsLock"].includes(event.key)
    ) {
      return;
    }
    document.activeElement?.removeAttribute("data-focus-quiet");
  },
  true
);

["pointerdown", "mousedown", "touchstart"].forEach((eventName) => {
  document.addEventListener(
    eventName,
    () => {
      lastInputWasKeyboard = false;
    },
    true
  );
});

function setFocus(element) {
  if (!element) return;

  if (lastInputWasKeyboard) {
    element.removeAttribute("data-focus-quiet");
  } else if (!element.hasAttribute("data-focus-quiet")) {
    element.setAttribute("data-focus-quiet", "");
    element.addEventListener(
      "blur",
      () => element.removeAttribute("data-focus-quiet"),
      { once: true }
    );
  }

  element.focus();

  // No blur ever fires when focus did not land, so the flag would linger
  if (document.activeElement !== element) {
    element.removeAttribute("data-focus-quiet");
  }
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return;

    const elements = getFocusableElements(container);
    if (elements.length === 0) return;

    const first = elements[0];
    const last = elements[elements.length - 1];
    const target = event.target;

    if (!container.contains(target)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey) {
      if (target === first || target === container) {
        event.preventDefault();
        last.focus();
      }
    } else if (target === last || target === container) {
      event.preventDefault();
      first.focus();
    }
  };

  trapFocusHandlers.focusin = (event) => {
    if (container.contains(event.target)) return;

    const elements = getFocusableElements(container);
    elements[0]?.focus();
  };

  document.addEventListener("keydown", trapFocusHandlers.keydown, true);
  document.addEventListener("focusin", trapFocusHandlers.focusin, true);

  setFocus(elementToFocus);
}

function pauseAllMedia(parentElement = document, exceptElement = null) {
  parentElement.querySelectorAll(".js-youtube").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  parentElement.querySelectorAll(".js-vimeo").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  parentElement.querySelectorAll("video").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.pause();
  });
  parentElement.querySelectorAll("product-model").forEach((model) => {
    if (exceptElement && exceptElement.contains(model)) return;
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin, true);
  document.removeEventListener("keydown", trapFocusHandlers.keydown, true);

  setFocus(elementToFocus);
}

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  cents = parseInt(cents, 10);

  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || theme.moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) {
    if (isNaN(number) || number == null) {
      return "0";
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ", ".");
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ",");
      break;
    case "amount_with_period_and_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    default:
      value = formatWithDelimiters(cents, 2, ",", ".");
  }

  return formatString.replace(placeholderRegex, value);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

function parseCartJSON(response) {
  return response.text().then((text) => {
    try {
      return JSON.parse(text);
    } catch (error) {
      throw new Error(`Unexpected cart response (status ${response.status})`);
    }
  });
}

function resolveCartSections(
  parsedState,
  sectionNames,
  sectionsUrl = window.location.pathname
) {
  if (parsedState && parsedState.sections) {
    return Promise.resolve(parsedState.sections);
  }

  const names = (sectionNames || []).filter(Boolean).join(",");
  if (!names) return Promise.resolve(null);

  const url = new URL(sectionsUrl, window.location.origin);
  url.searchParams.set("sections", names);

  return fetch(url.toString())
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
}

(function () {
  function copyURI(e, copyLink) {
    e.preventDefault();

    const url = window.location.href;

    const handleCopy = (btn, success) => {
      if (success) {
        btn.classList.add("copied");
        setTimeout(() => btn.classList.remove("copied"), 1000);
      } else {
        btn.classList.add("notCopied");
        setTimeout(() => btn.classList.remove("notCopied"), 1000);
      }
    };

    // Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => handleCopy(copyLink, true))
        .catch(() => handleCopy(copyLink, false));
    } else {
      // Fallback for unsupported browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed"; // avoid scrolling
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        handleCopy(copyLink, successful);
      } catch (err) {
        handleCopy(copyLink, false);
      }

      document.body.removeChild(textArea);
    }
  }

  const copyLinks = document.querySelectorAll(".copy-btn");
  copyLinks.forEach((copyLink) =>
    copyLink.addEventListener("click", (e) => copyURI(e, copyLink))
  );
})();

if (!Element.prototype.replaceChildren) {
  Element.prototype.replaceChildren = function (...nodes) {
    while (this.firstChild) this.removeChild(this.firstChild);
    this.append(...nodes);
  };
}

// [Theme custom event] - Cart UI refresh event
document.addEventListener("cart:refresh", (event) => {
  const sectionsToUpdate = [
    {
      id: "cart-icon-bubble",
      selector: ".cart-count-bubble",
    },
    {
      id: "cart-drawer",
      selector: "#CartDrawer",
    },
    {
      id: "main-cart",
      selector: ".main-cart",
    },
  ];

  const sectionIds = sectionsToUpdate.map((section) => section.id).join(",");

  fetch(`${routes.cart_url}?sections=${sectionIds}`)
    .then((response) => response.json())
    .then((sections) => {
      sectionsToUpdate.forEach((section) => {
        const sectionHTML = sections[section.id];
        if (!sectionHTML) return;

        const html = new DOMParser().parseFromString(sectionHTML, "text/html");
        const selector = section.selector;

        const sourceElement = html.querySelector(selector);
        const destinationElements = document.querySelectorAll(selector);

        if (sourceElement && destinationElements.length > 0) {
          destinationElements.forEach((destinationElement) => {
            destinationElement.innerHTML = sourceElement.innerHTML;
            if (sourceElement.classList.contains("is-empty")) {
              destinationElement.classList.add("is-empty");
            } else {
              destinationElement.classList.remove("is-empty");
            }
          });
        }
      });
    })
    .catch((error) => {
      console.error("Error updating cart:", error);
    });
});

/*
 * ------------ Theme Web Components ------------
 */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new Event("change", { bubbles: true });
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.input = this.querySelector("input");
    this.isCartItem = Boolean(this.closest(".cart-item"));
    this.rollField = this.querySelector(".js-quantity-roll");
    this.rollGhost = null;
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    this.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", this.onButtonClick.bind(this));
    });

    this.setMinimumDisable();

    var eventList = ["paste", "input"];
    for (const event of eventList) {
      this.input.addEventListener(event, (e) => {
        const value = e.currentTarget.value;
        const numberRegex = this.isCartItem ? /^\d*$/ : /^0*?[1-9]\d*$/;

        if (numberRegex.test(value) || value === "") {
        } else {
          e.currentTarget.value = 1;
        }

        this.setMinimumDisable();
      });
    }

    this.input.addEventListener("focusout", (e) => {
      if (e.currentTarget.value === "") {
        e.currentTarget.value = 1;
        this.setMinimumDisable();
      }
    });
  }

  setMinimumDisable() {
    const minusBtn = this.querySelector('button[name="minus"]');
    if (!minusBtn || !this.input) return;

    if (this.input.value == this.input.min) {
      minusBtn.classList.add("disabled");
    } else {
      minusBtn.classList.remove("disabled");
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.rollValue(previousValue, event.target.name);
      this.input.dispatchEvent(this.changeEvent);
    }

    this.setMinimumDisable();
  }

  rollValue(previousValue, buttonName) {
    if (!this.rollField || this.prefersReducedMotion.matches) return;

    this.clearRoll();

    const ghost = document.createElement("span");
    ghost.className = "quantity-roll__ghost";
    ghost.setAttribute("aria-hidden", "true");
    ghost.textContent = previousValue;

    this.rollGhost = ghost;
    this.rollField.appendChild(ghost);
    this.rollField.dataset.roll = buttonName === "plus" ? "up" : "down";
    void this.rollField.offsetWidth;
    this.rollField.classList.add("is-rolling");

    ghost.addEventListener(
      "animationend",
      () => {
        if (this.rollGhost !== ghost) return;
        this.clearRoll();
      },
      { once: true }
    );
  }

  clearRoll() {
    if (!this.rollField) return;

    this.rollField.classList.remove("is-rolling");
    if (this.rollGhost) {
      this.rollGhost.remove();
      this.rollGhost = null;
    }
  }
}

if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantityInput);
}

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetBtn = this.querySelector(".js-search-input-reset");

    this.input?.form?.addEventListener("reset", this.onFormReset.bind(this));
    this.input?.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.resetBtn?.addEventListener("click", this.onFormReset.bind(this));
  }

  connectedCallback() {
    this.toggleResetBtn(this.input?.value);
  }

  toggleResetBtn(value) {
    if (!this.resetBtn) return;

    if (value) {
      this.resetBtn.style.display = "flex";
    } else {
      this.resetBtn.style.display = "none";
    }
  }

  onChange(event) {
    this.toggleResetBtn(event.target.value);
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    if (this.input) {
      if (this.closest(".search-section") && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        const q = params.get("q");
        if (this.input.value !== q) {
          setTimeout(() => {
            this.input.value = q;
            this.input.focus();
            this.toggleResetBtn(this.input.value);
          }, 0);
        } else {
          const cleanURL = window.location.pathname;
          window.location.href = cleanURL;
        }
      } else {
        this.input.value = "";
        this.input.focus();
        this.toggleResetBtn("");
      }
    }
  }
}

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    this.closeBtn = this.querySelector(".modal-close-button");
    const summaryElements = this.querySelectorAll("summary");
    this.addAccessibilityAttributes(summaryElements);

    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );

    this.closeBtn?.addEventListener(
      "click",
      this.onCloseButtonClick.bind(this)
    );
  }

  addAccessibilityAttributes(summaryElements) {
    summaryElements.forEach((element) => {
      element.setAttribute("role", "button");
      element.setAttribute("aria-expanded", false);
      element.setAttribute("aria-controls", element.nextElementSibling.id);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute("open");

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );

      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
      });
    }
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-drawer`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", false);
      document.body.classList.remove(`overflow-hidden-drawer`);
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);
      if (this.headerWrapper) this.headerWrapper.preventHide = false;
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    if (!detailsElement) return;
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement?.classList.remove("menu-opening");
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get("menu-drawer")) {
  customElements.define("menu-drawer", MenuDrawer);
}

class HeaderMobileDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header = document.querySelector(".shopify-section-header");
    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.openBtn = this.querySelector(".js-menu-drawer-open");
    this.closeBtn = this.querySelector(".js-menu-drawer-close");
    this.isOpen = false;

    this.closeBtn?.addEventListener("click", () => {
      this.closeMenuDrawer(this.openBtn);
    });
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add("overflow-hidden-mobile-menu");
    this.isOpen = true;
    this.openScrollY = window.scrollY;

    // styles for forced disable body scroll on iOS
    if (typeof window.lockScrollBodyOnIos === "function") {
      this.openScrollY = window.scrollY;
      window.lockScrollBodyOnIos(this.openScrollY);
    }
  }

  closeMenuDrawer(elementToFocus) {
    if (!elementToFocus) return;
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    document.body.classList.remove("overflow-hidden-mobile-menu");
    document.body.classList.remove("overflow-hidden");
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // Collapse every drill-down panel, at any depth, so reopening the drawer always lands
    // on the first level instead of wherever the visitor left off.
    this.querySelectorAll(".mega-submenu-mobile--visible").forEach((panel) => {
      panel.classList.remove("mega-submenu-mobile--visible");
      panel.inert = true;
    });
    this.querySelectorAll(
      '.header-mobile-menu__link[aria-expanded="true"], .js-mobile-panel-trigger[aria-expanded="true"]'
    ).forEach((link) => {
      link.setAttribute("aria-expanded", "false");
    });

    // close search modal if open
    const searchModal = document.querySelector(".header-search-modal");
    if (searchModal && searchModal.getAttribute("open")) {
      const headerSearch = document.querySelector("header-search");
      if (headerSearch && typeof headerSearch.close === "function") {
        headerSearch.close(null, elementToFocus);
      }
    }

    // styles for forced disable body scroll on iOS
    if (typeof window.unlockScrollBodyOnIos === "function") {
      window.unlockScrollBodyOnIos(this.openScrollY);
    }

    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.isOpen = false;
  }
}

if (!customElements.get("header-mobile-drawer")) {
  customElements.define("header-mobile-drawer", HeaderMobileDrawer);
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll(
      '[id^="ModalCloseBtn-"], [id^="ModalCloseOverlay-"]'
    ).forEach((el) => el.addEventListener("click", () => this.hide(false)));

    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        ) {
          this.hide();
        }
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    document.querySelectorAll("modal-dialog").forEach((modal) => {
      if (modal !== this && typeof modal.snapHidden === "function") {
        modal.snapHidden();
      }
    });

    document
      .querySelectorAll("modal-opener.is-modal-open")
      .forEach((el) => el.classList.remove("is-modal-open"));
    opener?.closest("modal-opener")?.classList.add("is-modal-open");

    this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = "eager";
    });

    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    this.addEventListener(
      "transitionend",
      () => {
        if (this.hasAttribute("open")) {
          trapFocus(this, this.querySelector('[role="dialog"]'));
        }
      },
      { once: true }
    );
  }

  snapHidden() {
    if (this.hasAttribute("open")) return;
    if (getComputedStyle(this).visibility === "hidden") return;

    this.classList.add("modal--instant");
    void this.offsetWidth;
    requestAnimationFrame(() => this.classList.remove("modal--instant"));
  }

  hide() {
    this.removeAttribute("open");

    const opener = this.openedBy?.closest("modal-opener");
    if (opener?.classList.contains("is-modal-open")) {
      opener.classList.remove("is-modal-open");
      opener.dispatchEvent(new PointerEvent("pointerleave"));
    }

    removeTrapFocus(this.openedBy);
    document.body.classList.remove("overflow-hidden");
  }
}

if (!customElements.get("modal-dialog")) {
  customElements.define("modal-dialog", ModalDialog);
}

class ProductModal extends ModalDialog {
  constructor() {
    super();
  }
}

if (!customElements.get("product-modal")) {
  customElements.define("product-modal", ProductModal);
}

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });

    this.addEventListener("pointerenter", this.warmMedia, { once: true });
    this.addEventListener("focusin", this.warmMedia, { once: true });
  }

  warmMedia = () => {
    const modal = document.querySelector(this.getAttribute("data-modal"));

    modal?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = "eager";
    });
  };
}

if (!customElements.get("modal-opener")) {
  customElements.define("modal-opener", ModalOpener);
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) {
      poster.addEventListener("click", this.loadContent.bind(this));
    }

    if (this.dataset.autoplay === "true") {
      this.loadContent();
    }
  }

  loadContent() {
    const parentEl =
      this.closest(".product__media") || this.closest(".product-media-modal");
    pauseAllMedia(parentEl);

    if (this.getAttribute("loaded")) return;

    const template = this.querySelector("template");
    if (!template) return;

    const content = document.createElement("div");
    content.appendChild(template.content.firstElementChild.cloneNode(true));
    this.setAttribute("loaded", true);

    const media = content.querySelector("video, model-viewer, iframe");
    if (!media) return;

    const deferredElement = this.appendChild(media);

    if (["VIDEO", "IFRAME"].includes(deferredElement.nodeName)) {
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            let shouldAutoplay = false;
            const parentSlide = entry.target.closest(".swiper-slide");
            if (parentSlide) {
              if (parentSlide.classList.contains("swiper-slide-active")) {
                shouldAutoplay = true;
              }
            } else {
              if (entry.intersectionRatio > 0.7) {
                shouldAutoplay = true;
              }
            }

            if (shouldAutoplay) {
              if (
                (this.dataset.autoplay === "true" &&
                  !this.closest("product-modal")) ||
                (this.dataset.autoplay === "true" &&
                  this.closest("product-modal")?.hasAttribute("open"))
              ) {
                pauseAllMedia(parentEl, this);

                if (entry.target.nodeName === "VIDEO") {
                  entry.target.play()?.catch((error) => {
                    console.error("Error playing video:", error);
                  });
                } else if (
                  entry.target.nodeName === "IFRAME" &&
                  entry.target.contentWindow
                ) {
                  if (entry.target.classList.contains("js-youtube")) {
                    entry.target.contentWindow.postMessage(
                      '{"event":"command","func":"playVideo","args":""}',
                      "*"
                    );
                  } else {
                    entry.target.contentWindow.postMessage(
                      '{"method":"play"}',
                      "*"
                    );
                  }
                }
              }
            } else {
              if (entry.target.nodeName === "VIDEO") {
                entry.target.pause();
              } else if (
                entry.target.nodeName === "IFRAME" &&
                entry.target.contentWindow
              ) {
                if (entry.target.classList.contains("js-youtube")) {
                  entry.target.contentWindow.postMessage(
                    '{"event":"command","func":"pauseVideo","args":""}',
                    "*"
                  );
                } else {
                  entry.target.contentWindow.postMessage(
                    '{"method":"pause"}',
                    "*"
                  );
                }
              }
            }
          });
        },
        {
          threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
        }
      );

      this.videoObserver.observe(deferredElement);
    }

    const modelViewer = this.closest(".swiper-slide-active")?.querySelector(
      "model-viewer"
    );
    if (
      modelViewer &&
      !modelViewer.classList.contains("shopify-model-viewer-ui__disabled")
    ) {
      const swiper = this.closest(".swiper")?.swiper;
      if (swiper) {
        swiper.params.noSwiping = true;
        swiper.params.noSwipingClass = "swiper-slide";
      }
    }

    this.querySelectorAll(
      "video, .shopify-model-viewer-ui__controls-overlay"
    ).forEach((media) => {
      if (this.getAttribute("loaded") === "true") {
        media.addEventListener("click", (event) => {
          pauseAllMedia(parentEl, this);
        });
      }
    });
  }

  disconnectedCallback() {
    this.videoObserver?.disconnect();
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

class ProductModel extends DeferredMedia {
  constructor() {
    super();
  }

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    this.modelViewerUI = new Shopify.ModelViewerUI(
      this.querySelector("model-viewer")
    );

    const $this = this;
    const target = $this.querySelector("model-viewer");

    this.modelViewerObserver = new MutationObserver((records) => {
      if ($this.closest(".swiper") && $this.closest(".swiper").swiper) {
        const swiperObj = $this.closest(".swiper").swiper;
        if (!target.classList.contains("shopify-model-viewer-ui__disabled")) {
          swiperObj.params.noSwiping = true;
          swiperObj.params.noSwipingClass = "swiper-slide";
        } else {
          swiperObj.params.noSwiping = false;
        }
      }
    });

    this.modelViewerObserver.observe(target, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.modelViewerObserver?.disconnect();
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: "shopify-xr",
        version: "1.0",
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", () =>
        this.setupShopifyXR()
      );
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener("DOMContentLoaded", () => {
  // Only pages that ship model JSON need the XR bundle; the quick view loads it on its own.
  if (document.querySelector('[id^="ProductJSON-"]')) {
    window.ProductModel?.loadShopifyXR();
  }
});

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.addEventListener("change", this.onVariantChange.bind(this));

    this.variantMediaDisplay =
      this.closest(".product")?.querySelector(".product__media")?.dataset
        ?.variantMediaDisplay || "show_all";
  }

  onVariantChange(event) {
    if (!this.contains(event.target)) return;

    const selectedValuesIds = this.getSelectedValuesIds();
    const requestUrl = this.createRequestUrl({
      selectedValuesIds: selectedValuesIds,
      connectedProductURL: this.connectedProductURL,
    });

    this.fetchProductInfo(requestUrl);

    this.resetErrorMessage();
  }

  getSelectedValuesIds() {
    const controls = [...this.querySelectorAll(".product-form__controls")];

    controls.sort((a, b) => {
      return (
        Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );
    });

    return controls.map((control) => {
      const checkedInput = control.querySelector('input[type="radio"]:checked');

      // Supporting combined listings: If the checked input has a connected product url, save it to use in the request
      if (checkedInput?.dataset?.connectedProductUrl) {
        this.connectedProductURL = checkedInput.dataset.connectedProductUrl;
      }
      return checkedInput?.dataset?.optionValueId
        ? checkedInput.dataset.optionValueId
        : null;
    });
  }

  createRequestUrl({ selectedValuesIds = [], connectedProductURL = "" }) {
    const productUrl = connectedProductURL || `${this.dataset.url}`;
    const sectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    if (selectedValuesIds.length) {
      const params = [];
      params.push(`section_id=${sectionId}`);
      params.push(`option_values=${selectedValuesIds.join(",")}`);
      return `${productUrl}?${params.join("&")}`;
    }
  }

  fetchProductInfo(requestUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");

        try {
          // Search this.currentVariant
          this.setCurrentVariantAfterFetch(html);

          this.updateURL();
          this.updateVariantInput();
          this.updatePickupAvailability();

          if (
            this.currentVariant.featured_media &&
            this.variantMediaDisplay === "show_all"
          ) {
            // If variant display != "show_all", the media gallery element is fully replaced inside updateElementsAfterFetch
            const mediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;
            this.updateMedia(mediaId);
          }
        } catch (err) {}

        this.updateElementsAfterFetch(html);

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings.soldOut
          );
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.info("Fetch aborted by user");
        } else {
          console.error(error);
        }
      })
      .finally(() => {});
  }

  setCurrentVariantAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    if (!variantPickerSource) return;

    const newVariantDataEl = variantPickerSource.querySelector(
      "[data-selected-variant]"
    );
    if (!newVariantDataEl) return;

    const newVariantData = variantPickerSource.querySelector(
      "[data-selected-variant]"
    ).innerHTML;

    const selectedVariant = !!newVariantData
      ? JSON.parse(newVariantData)
      : null;

    this.currentVariant = selectedVariant;
  }

  updateURL() {
    if (this.dataset.updateUrl === "false") return;
    const newUrl = this.currentVariant
      ? `${this.dataset.url}?variant=${this.currentVariant.id}`
      : this.dataset.url;

    window.history.replaceState({}, "", newUrl);
  }

  updateVariantInput() {
    if (!this.currentVariant?.id) return;

    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));

      // Forms without a visible quantity selector (sticky bar / hidden
      // selector) carry a hidden quantity input — keep it at the variant's
      // minimum orderable quantity so add to cart passes the quantity rules.
      // No quantity_rule in the variant JSON -> keep the server-rendered value
      const hiddenQuantityInput = productForm.querySelector(
        'input[type="hidden"][name="quantity"]'
      );
      const quantityRule = this.currentVariant.quantity_rule;
      if (hiddenQuantityInput && quantityRule) {
        hiddenQuantityInput.value = Math.max(
          quantityRule.min || 1,
          quantityRule.increment || 1
        );
      }

      const stickyQuantityInput = productForm.querySelector(
        ".product-sticky-add-bar__quantity .quantity__input"
      );
      if (stickyQuantityInput && quantityRule) {
        const min = Math.max(
          quantityRule.min || 1,
          quantityRule.increment || 1
        );
        stickyQuantityInput.min = min;
        stickyQuantityInput.step = quantityRule.increment || 1;
        stickyQuantityInput.dataset.min = min;

        if (quantityRule.max) {
          stickyQuantityInput.max = quantityRule.max;
          stickyQuantityInput.dataset.max = quantityRule.max;
        } else {
          stickyQuantityInput.removeAttribute("max");
          delete stickyQuantityInput.dataset.max;
        }

        const current = Number(stickyQuantityInput.value) || 0;
        const max = quantityRule.max ? Number(quantityRule.max) : Infinity;
        if (current < min || current > max) {
          stickyQuantityInput.value = min;
        }
      }
    });

    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        sectionId: this.dataset.section,
        variant: this.currentVariant,
      },
    });

    // [Start theme custom event] - Variant change event
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: {
          variant: this.currentVariant,
          previousVariant: this.previousVariant || null,
          formElement: this,
          sectionId: this.dataset.section,
        },
      })
    );

    this.previousVariant = this.currentVariant;
    // [End theme custom event] - Variant change event
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelectorAll(
      `pickup-availability[data-section="${this.dataset.section}"]`
    );
    if (!pickUpAvailability.length) return;
    pickUpAvailability.forEach((el) => {
      if (this.currentVariant && this.currentVariant.available) {
        el.fetchAvailability(this.currentVariant.id);
      } else {
        el.cancelFetch?.();
        el.removeAttribute("available");
        el.innerHTML = "";
      }
    });
  }

  updateMedia(mediaId) {
    if (!mediaId) return;

    const mediaEl = document.querySelector(
      `[data-section="product-media-${this.dataset.section}"]`
    );
    if (!mediaEl) return;

    // for slider and slider_previews layout
    const sliderEl = mediaEl.querySelector(".js-media-list");
    if (sliderEl && sliderEl.swiper) {
      const slideCount = sliderEl.querySelectorAll(".swiper-slide").length;
      const speed = slideCount > 32 ? 0 : 800;
      sliderEl.querySelectorAll(".swiper-slide").forEach((slide, index) => {
        const slideMediaId = slide.dataset?.mediaId;
        if (slideMediaId === mediaId) {
          const findIndex = sliderEl.swiper.slides.findIndex((slideEl) => {
            return slideEl.dataset?.mediaId === slideMediaId;
          });
          const fallbackIndex = Number(
            slide.dataset?.swiperSlideIndex || index
          );
          const swiperSlideIndex = findIndex !== -1 ? findIndex : fallbackIndex;
          sliderEl.swiper.slideTo(swiperSlideIndex, speed);
        }
      });
    }

    const quickViewHost = this.closest("quick-view-modal");
    if (quickViewHost && typeof quickViewHost.promoteMedia === "function") {
      quickViewHost.promoteMedia(mediaId);
    }

    // for stacked and grid layout
    const stackedOrGridEl =
      mediaEl.querySelector(
        ".product__media-list[data-desktop-type='stacked']"
      ) ||
      mediaEl.querySelector(".product__media-list[data-desktop-type='grid']") ||
      mediaEl.querySelector(".product__media-list[data-mobile-type='grid']");
    if (stackedOrGridEl) {
      stackedOrGridEl
        .querySelectorAll("[data-media-id].product__media-item")
        .forEach((mediaItem) => {
          const mediaItemId = mediaItem.dataset?.mediaId;
          if (mediaItemId === mediaId) {
            mediaItem.classList.add("stacked-or-grid-active");
          } else {
            mediaItem.classList.remove("stacked-or-grid-active");
          }
        });
    }
  }

  updateElementsAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const currentSectionId = this.dataset.section;
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    // Price
    const priceSource = html.querySelector(
      `[data-id="Price-${sourceSectionId}"]`
    );
    const priceDestination = document.querySelectorAll(
      `[data-id="Price-${currentSectionId}"]`
    );
    if (priceSource) {
      priceDestination.forEach((el) => {
        el.innerHTML = priceSource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Price in sticky add to cart bar
    const priceStickySource = html.querySelector(
      `[data-id="PriceSticky-${sourceSectionId}"]`
    );
    const priceStickyDestination = document.querySelectorAll(
      `[data-id="PriceSticky-${currentSectionId}"]`
    );
    if (priceStickySource) {
      priceStickyDestination.forEach((el) => {
        el.innerHTML = priceStickySource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Inventory
    const inventorySource = html.querySelector(
      `[data-id="Inventory-${sourceSectionId}"]`
    );
    const inventoryDestination = document.querySelectorAll(
      `[data-id="Inventory-${currentSectionId}"]`
    );
    if (inventorySource) {
      inventoryDestination.forEach((el) => {
        el.innerHTML = inventorySource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          inventorySource.innerText === ""
        );
      });
    }

    // Store availability (Find store drawer) — the list is per variant
    const storeAvailabilitySource = html.querySelector(
      `[data-id="StoreAvailability-${sourceSectionId}"]`
    );
    const storeAvailabilityDestination = document.querySelectorAll(
      `[data-id="StoreAvailability-${currentSectionId}"]`
    );
    if (storeAvailabilitySource) {
      storeAvailabilityDestination.forEach((el) => {
        el.innerHTML = storeAvailabilitySource.innerHTML;
      });
    }

    // SKU
    const skuSource = html.querySelector(`[data-id="Sku-${sourceSectionId}"]`);
    const skuDestination = document.querySelectorAll(
      `[data-id="Sku-${currentSectionId}"]`
    );
    if (skuSource) {
      skuDestination.forEach((el) => {
        el.innerHTML = skuSource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          skuSource.classList.contains("visibility-hidden")
        );
      });
    }

    // Variant picker: Color swatches label. Keyed on the option handle, not on
    // index — a section can hold several pickers (details + sticky bar) that all
    // read from the one source label.
    const colorNamePrefix = `ColorName-${sourceSectionId}-`;
    const colorNameSourceByOption = new Map();
    html
      .querySelectorAll(`[data-id^="ColorName-${sourceSectionId}"]`)
      .forEach((el) => {
        colorNameSourceByOption.set(
          el.dataset.id.replace(colorNamePrefix, ""),
          el
        );
      });
    document
      .querySelectorAll(`[data-id^="ColorName-${currentSectionId}"]`)
      .forEach((colorNameDestination) => {
        const source = colorNameSourceByOption.get(
          colorNameDestination.dataset.id.replace(
            `ColorName-${currentSectionId}-`,
            ""
          )
        );
        if (!source) return;
        colorNameDestination.classList.toggle(
          "visibility-hidden",
          source.classList.contains("visibility-hidden")
        );
        colorNameDestination.innerHTML = source.innerHTML;
      });

    // Variant picker: Options
    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    const variantPickerDestinations = document.querySelectorAll(
      `[data-id="VariantPicker-${currentSectionId}"]`
    );
    const quickViewModalHost = this.closest("quick-view-modal");
    if (variantPickerSource) {
      const quickViewModal = this.closest("quick-view-modal");
      const sourceOptionValues = variantPickerSource.querySelectorAll(
        `[data-id^="OptionValues-${sourceSectionId}-"]`
      );
      const sourceCurrentVariantScript = variantPickerSource.querySelector(
        "[data-selected-variant]"
      );

      // Every picker in the section is refreshed from the same markup, so the
      // input ids / radio names it carries are re-scoped to the destination —
      // otherwise two pickers would share one radio group and only the last one
      // would stay checked.
      const sourcePickerScope = variantPickerSource.dataset.pickerScope;
      const rescopeToDestination = (markup, destinationEl) => {
        const destScope = destinationEl.dataset.pickerScope;
        if (
          !sourcePickerScope ||
          !destScope ||
          sourcePickerScope === destScope
        ) {
          return markup;
        }
        return markup.replaceAll(sourcePickerScope, destScope);
      };

      if (sourceOptionValues.length && !this.connectedProductURL) {
        variantPickerDestinations.forEach((pickerDestEl) => {
          sourceOptionValues.forEach((sourceOptionsEl) => {
            const position = sourceOptionsEl.dataset.optionPosition;
            if (!position) return;
            const destOptionsEl = pickerDestEl.querySelector(
              `[data-option-position="${position}"][data-id^="OptionValues-"]`
            );
            if (destOptionsEl) {
              const focus = this.rememberFocus(destOptionsEl);
              destOptionsEl.innerHTML = quickViewModal
                ? sourceOptionsEl.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : rescopeToDestination(sourceOptionsEl.innerHTML, pickerDestEl);
              this.restoreFocus(destOptionsEl, focus);
            }
          });

          if (sourceCurrentVariantScript) {
            const destCurrentVariantScript = pickerDestEl.querySelector(
              "[data-selected-variant]"
            );
            if (destCurrentVariantScript) {
              destCurrentVariantScript.innerHTML =
                sourceCurrentVariantScript.innerHTML;
            }
          }
        });
      } else {
        // fallback to replace whole variant picker html
        if (quickViewModal) {
          variantPickerDestinations.forEach((el) => {
            const focus = this.rememberFocus(el);
            el.innerHTML = variantPickerSource.innerHTML.replaceAll(
              sourceSectionId,
              `quickview-${sourceSectionId}`
            );
            this.restoreFocus(el, focus);
          });
        } else {
          variantPickerDestinations.forEach((el) => {
            const focus = this.rememberFocus(el);
            el.innerHTML = rescopeToDestination(
              variantPickerSource.innerHTML,
              el
            );
            this.restoreFocus(el, focus);
          });
        }
      }
    }

    if (
      quickViewModalHost &&
      typeof quickViewModalHost.stripBlockedElements === "function"
    ) {
      quickViewModalHost.stripBlockedElements(quickViewModalHost);
    }

    // Update elements for B2B quantity pricing https://help.shopify.com/en/manual/b2b/catalogs/quantity-pricing
    const productButtonsSource = html.querySelector(
      `[data-id="ProductButtons-${sourceSectionId}"]`
    );
    const productButtonsDestination = document.querySelectorAll(
      `[data-id="ProductButtons-${currentSectionId}"]`
    );
    if (productButtonsSource) {
      const sourceButtonsWrapper = productButtonsSource.querySelector(
        ".product-form__buttons"
      );
      productButtonsDestination.forEach((el) => {
        const buttonsWrapper = el.querySelector(".product-form__buttons");

        if (sourceButtonsWrapper && buttonsWrapper) {
          const sourceHasQuantityPricing =
            sourceButtonsWrapper.dataset.hasQuantityPricing === "true";
          const destHadQuantityPricing =
            buttonsWrapper.dataset.hasQuantityPricing === "true";

          // This function is executed if quantity pricing is configured for the product or product variant
          if (sourceHasQuantityPricing || destHadQuantityPricing) {
            if (sourceHasQuantityPricing) {
              buttonsWrapper.dataset.hasQuantityPricing = "true";
            } else {
              buttonsWrapper.removeAttribute("data-has-quantity-pricing");
            }

            // Update b2b elements (volume pricing + quantity rules)
            const sourceB2bEl = sourceButtonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            const destB2bEl = buttonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            if (sourceB2bEl && destB2bEl) {
              destB2bEl.innerHTML = sourceB2bEl.innerHTML;
            } else if (sourceB2bEl && !destB2bEl) {
              buttonsWrapper.insertAdjacentHTML(
                "afterbegin",
                sourceB2bEl.outerHTML
              );
            } else if (!sourceB2bEl && destB2bEl) {
              destB2bEl.remove();
            }

            // Update quantity input
            const sourceQuantityWrapper = sourceButtonsWrapper.querySelector(
              ".product-form__quantity"
            );
            const destQuantityWrapper = buttonsWrapper.querySelector(
              ".product-form__quantity"
            );
            if (sourceQuantityWrapper && destQuantityWrapper) {
              destQuantityWrapper.innerHTML =
                sourceQuantityWrapper.innerHTML.replaceAll(
                  sourceSectionId,
                  currentSectionId
                );
            }
          }
        }
      });
    }

    // Product media
    // Product media is replaced with DOM changes and re-initialization of initProductMediaGallery only when Variant media display is not set to "Show all" or when a product is linked to the variant (e.g., via Combined listings).
    // In most other cases, it updates via updateMedia without DOM changes.
    if (this.variantMediaDisplay !== "show_all" || this.connectedProductURL) {
      const mediaSource = html.querySelector(
        `[data-section="product-media-${sourceSectionId}"]`
      );
      const mediaDestination = document.querySelector(
        `[data-section="product-media-${currentSectionId}"]`
      );
      if (mediaSource && mediaDestination) {
        mediaDestination.innerHTML = mediaSource.innerHTML;

        const parentQuickView = this.closest("quick-view-modal");
        if (parentQuickView) {
          if (typeof parentQuickView.removeDOMElements === "function") {
            parentQuickView.removeDOMElements(mediaDestination);
          }
          if (typeof parentQuickView.initSlider === "function") {
            parentQuickView.initSlider();
          }
        } else {
          // Update zoom modal for Combined listings that was moved to body by ModalDialog.connectedCallback
          if (this.connectedProductURL) {
            const zoomModalSource = html.querySelector(
              `#ProductModal-${sourceSectionId}`
            );
            const zoomModalDest = document.querySelector(
              `#ProductModal-${currentSectionId}`
            );

            if (zoomModalSource && zoomModalDest) {
              const newZoomModal = zoomModalSource.cloneNode(true);
              newZoomModal.id = `ProductModal-${currentSectionId}`;
              zoomModalDest.replaceWith(newZoomModal);
            }
          }

          const section = document.getElementById(
            `shopify-section-${currentSectionId}`
          );

          if (section && typeof window.initProductMediaGallery === "function") {
            window.initProductMediaGallery(section);
          }
        }
      }
    }

    // Badges
    const badgesSrc = html.querySelector(
      `#ProductInfo-${sourceSectionId} .product__badges`
    );
    const badgesDest = document.querySelectorAll(
      `#ProductInfo-${currentSectionId} .product__badges`
    );
    if (badgesSrc) {
      badgesDest.forEach((el) => {
        el.innerHTML = badgesSrc.innerHTML;
      });
    }

    // Replace other elements for Combined listings
    if (this.connectedProductURL) {
      // ...Src - source elements from response HTML
      // ...Dest - destination elements in the current DOM
      const productInfoSrc = html.getElementById(
        `ProductInfo-${sourceSectionId}`
      );
      const productInfoDest = document.getElementById(
        `ProductInfo-${currentSectionId}`
      );
      if (productInfoSrc && productInfoDest) {
        // Title
        const titleSrc = productInfoSrc.querySelector(".product__title");
        const titleDest = productInfoDest.querySelectorAll(".product__title");
        if (titleSrc) {
          titleDest.forEach((el) => {
            el.innerHTML = titleSrc.innerHTML;
          });
        }

        // Vendor
        const vendorSrc = productInfoSrc.querySelector(".product__vendor");
        const vendorDest = productInfoDest.querySelectorAll(".product__vendor");
        if (vendorSrc) {
          vendorDest.forEach((el) => {
            el.innerHTML = vendorSrc.innerHTML;
          });
        }

        // Description
        const descriptionSrc = productInfoSrc.querySelector(
          ".product__description"
        );
        const descriptionDest = productInfoDest.querySelectorAll(
          ".product__description"
        );
        if (descriptionSrc) {
          descriptionDest.forEach((el) => {
            el.innerHTML = descriptionSrc.innerHTML;
            el.classList.toggle(
              "hidden",
              descriptionSrc.classList.contains("hidden")
            );
          });
        }
      }

      // Complementary products
      const complementaryProductsSrc = productInfoSrc.querySelector(
        "product-recommendations.complementary-products"
      );
      const complementaryProductsDest = productInfoDest.querySelectorAll(
        "product-recommendations.complementary-products"
      );
      if (complementaryProductsSrc) {
        complementaryProductsDest.forEach((el) => {
          el.replaceWith(complementaryProductsSrc.cloneNode(true));
        });
      }
    }
  }

  // Swapping the option markup drops focus to <body>, so keyboard focus is
  // handed to the matching new control. Input ids carry product.id, which a
  // combined-listing swap changes — radios fall back to option position + value.
  rememberFocus(container) {
    const active = document.activeElement;
    if (!active || !container.contains(active)) return null;
    if (active.id) {
      return {
        id: active.id,
        position: active.closest("[data-option-position]")?.dataset
          .optionPosition,
        value: active.type === "radio" ? active.value : null,
      };
    }
    const index = [
      ...container.querySelectorAll(".dropdown-select__current"),
    ].indexOf(active);
    return index >= 0 ? { index } : null;
  }

  restoreFocus(container, focus) {
    if (!focus) return;
    let target = focus.id
      ? container.querySelector(`#${CSS.escape(focus.id)}`)
      : container.querySelectorAll(".dropdown-select__current")[focus.index];
    if (!target && focus.position && focus.value !== null) {
      target = container.querySelector(
        `[data-option-position="${CSS.escape(focus.position)}"] input[type="radio"][value="${CSS.escape(focus.value)}"]`
      );
    }
    if (target && target !== document.activeElement) {
      target.focus({ preventScroll: true });
    }
  }

  toggleAddButton(disable = true, text) {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;

      const addButtonText = addButton.querySelector(".js-change-button-text");

      if (disable) {
        addButton.setAttribute("disabled", true);
        addButton.setAttribute("aria-disabled", true);
        if (text && addButtonText) {
          addButtonText.textContent = text;
          addButtonText.dataset.label = text;

          if (text === window.variantStrings.unavailable) {
            addButton.dataset.status = "unavailable";
          } else {
            addButton.dataset.status = "sold-out";
          }
        }
      } else {
        addButton.removeAttribute("disabled");
        addButton.removeAttribute("aria-disabled");
        if (addButtonText) {
          addButtonText.textContent = window.variantStrings.addToCart;
          addButtonText.dataset.label = window.variantStrings.addToCart;
        }
        addButton.dataset.status = "available";
      }
    });
  }

  setUnavailable() {
    const price = document.querySelectorAll(
      `[data-id="Price-${this.dataset.section}"]`
    );
    const priceSticky = document.querySelectorAll(
      `[data-id="PriceSticky-${this.dataset.section}"]`
    );
    const inventory = document.querySelectorAll(
      `[data-id="Inventory-${this.dataset.section}"]`
    );
    const sku = document.querySelectorAll(
      `[data-id="Sku-${this.dataset.section}"]`
    );
    const colorName = document.querySelectorAll(
      `[data-id^="ColorName-${this.dataset.section}"]`
    );

    this.toggleAddButton(true, window.variantStrings.unavailable);

    price.forEach((el) => el.classList.add("visibility-hidden"));
    priceSticky.forEach((el) => el.classList.add("visibility-hidden"));
    inventory.forEach((el) => el.classList.add("visibility-hidden"));
    sku.forEach((el) => el.classList.add("visibility-hidden"));
    colorName.forEach((el) => el.classList.add("visibility-hidden"));
  }

  resetErrorMessage() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const parentEl =
        productForm.closest(".product-sticky-add-bar") ||
        productForm.closest("product-form");
      if (parentEl) {
        const errorWrapperEl = parentEl.querySelector(
          ".product-form__error-message-wrapper"
        );
        const errorTextEl = errorWrapperEl?.querySelector(
          ".product-form__error-message"
        );
        if (!errorWrapperEl || !errorTextEl) return;
        errorWrapperEl.setAttribute("hidden", true);
        errorTextEl.textContent = "";
      }
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}

class VariantDropdownSelect extends HTMLElement {
  constructor() {
    super();

    this.currentEl = this.querySelector(".dropdown-select__current");
    this.optionsWrapperEl = this.querySelector(".dropdown-select__options");
    this.hiddenInput = this.querySelector("input[type='hidden']");
    if (!this.currentEl || !this.optionsWrapperEl || !this.hiddenInput) return;

    this.optionsEls = Array.from(this.optionsWrapperEl.querySelectorAll("li"));
    this.inputs = Array.from(this.optionsWrapperEl.querySelectorAll("input"));
    this.hasColorSwatch = this.currentEl?.classList?.contains(
      "dropdown-select__current--with-color"
    );
    this.isActive = false;

    this.optionPosition = this.getOptionPosition();
    this._onCurrentClick = this.onCurrentClick.bind(this);
    this._onKeyUp = this.onKeyUp.bind(this);
    this._onOptionsClick = this.onClickOption.bind(this);
    this._onKeyUpOptions = this.onKeyUpOptions.bind(this);
    this._onOutsideClick = this.onOutsideClick.bind(this);
  }

  connectedCallback() {
    this.currentEl.addEventListener("click", this._onCurrentClick);
    this.currentEl.addEventListener("keyup", this._onKeyUp);
    this.currentEl.addEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.addEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.addEventListener("keyup", this._onKeyUpOptions);
    document.addEventListener("click", this._onOutsideClick);
  }

  disconnectedCallback() {
    this.currentEl.removeEventListener("click", this._onCurrentClick);
    this.currentEl.removeEventListener("keyup", this._onKeyUp);
    this.currentEl.removeEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.removeEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.removeEventListener("keyup", this._onKeyUpOptions);
    document.removeEventListener("click", this._onOutsideClick);
  }

  onCurrentClick(event) {
    event.stopPropagation();
    if (this.isActive) {
      this.onClose();
    } else {
      this.onOpen();
    }
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === "ENTER") {
      event.preventDefault();
      event.stopPropagation();
      if (this.isActive) {
        this.onClose();
      } else {
        this.onOpen();
      }
    }
  }

  preventDoubleClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onClickOption(event) {
    const optionEl = event.target.closest("li");
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (
      !optionEl ||
      !currentValueEl ||
      !this.optionsWrapperEl.contains(optionEl)
    ) {
      event.preventDefault();
      return;
    }

    const newValue = optionEl.dataset.value;

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;

    if (this.hasColorSwatch) {
      const newColor = optionEl.dataset.color;
      this.hiddenInput.dataset.colorSwatch = newColor;
      this.currentEl.style.setProperty("--swatch-color", newColor);
    }

    this.hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));

    this.inputs.forEach((el) => el.removeAttribute("checked"));
    if (event.target.tagName === "INPUT") {
      event.target.setAttribute("checked", "true");
    }

    this.onClose();
    this.currentEl.focus();
  }

  onKeyUpOptions(event) {
    if (event.code === "Escape" && this.isActive) {
      event.preventDefault();
      this.onClose();
      this.currentEl.focus();
    }
  }

  onOpen() {
    document.querySelectorAll("variant-dropdown-select").forEach((dropdown) => {
      if (dropdown !== this && typeof dropdown.onClose === "function") {
        dropdown.onClose();
      }
    });

    this.optionsWrapperEl.classList.add("active");
    this.currentEl.setAttribute("aria-expanded", "true");
    this.isActive = true;

    const currentIndex = this.inputs.findIndex((inp) =>
      inp.hasAttribute("checked")
    );
    this.inputs[currentIndex >= 0 ? currentIndex : 0]?.focus();
  }

  onClose() {
    this.optionsWrapperEl.classList.remove("active");
    this.currentEl.setAttribute("aria-expanded", "false");
    this.isActive = false;
  }

  onOutsideClick(event) {
    if (!this.isActive || this.contains(event.target)) return;
    this.onClose();
  }

  getOptionPosition() {
    const parentFieldset = this.closest(".product-form__controls");
    return Number(parentFieldset?.dataset?.optionPosition || -1);
  }

  updateCurrentOption(selectedValues = []) {
    // Method is used to synchronize after changes options in sticky bar.
    if (this.optionPosition === -1) return;
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (!currentValueEl || !selectedValues || !selectedValues.length) return;

    const newValue = selectedValues[this.optionPosition - 1];

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;

    this.optionsEls.forEach((optionEl) => {
      const liDataValue = optionEl.dataset.value;
      if (newValue === liDataValue && this.hasColorSwatch) {
        const newColor = optionEl.dataset.color;
        this.hiddenInput.dataset.colorSwatch = newColor;
        this.currentEl.style.setProperty("--swatch-color", newColor);
      }
    });
  }
}

if (!customElements.get("variant-dropdown-select")) {
  customElements.define("variant-dropdown-select", VariantDropdownSelect);
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    if (this.querySelector("form")) {
      this.form = this.querySelector("form");
      this.form.querySelector("[name=id]").disabled = false;
      this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
    } else {
      if (this.querySelector("[name=id]")) {
        this.querySelector("[name=id]").disabled = false;
      }
      this.querySelector("button[type=submit]")?.addEventListener(
        "click",
        this.onSubmitHandler.bind(this)
      );
    }

    this.cartUIElement =
      this.closest("cart-drawer") ||
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    this.submitButton = this.querySelector('[type="submit"]');
    if (this.cartUIElement) {
      this.submitButton.setAttribute("aria-haspopup", "dialog");
    }

    this.hideErrors = this.dataset.hideErrors === "true";
  }

  getStickyBarMainForm() {
    const formId = this.form?.getAttribute("id") || "";
    if (!formId.startsWith("sticky-bar-product-form-")) return null;

    return document.getElementById(
      formId.replace("sticky-bar-product-form-", "product-form-")
    );
  }

  getCustomPropertyFields(form) {
    if (!form) return [];

    return Array.from(form.elements).filter(
      (element) =>
        element.name?.startsWith("properties[") &&
        element.closest(".product-custom-property")
    );
  }

  validateStickyBarProperties() {
    const invalidField = this.getCustomPropertyFields(
      this.getStickyBarMainForm()
    ).find((field) => !field.checkValidity());

    if (!invalidField) return true;

    invalidField.reportValidity();
    return false;
  }

  appendStickyBarProperties(formData) {
    this.getCustomPropertyFields(this.getStickyBarMainForm()).forEach(
      (field) => {
        if (field.type === "checkbox" && !field.checked) return;
        if (field.value.trim() === "") return;
        formData.append(field.name, field.value);
      }
    );
  }

  removeBlankCustomProperties(formData) {
    this.getCustomPropertyFields(this.form).forEach((field) => {
      if (field.value.trim() !== "") return;
      formData.delete(field.name);
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute("aria-disabled") === "true") return;
    if (!this.validateStickyBarProperties()) return;

    this.handleErrorMessage();

    this.submitButton.classList.add("loading");
    const btnSpinner =
      this.submitButton.querySelector(".button-loader") ||
      this.submitButton.querySelector(".loading-overlay__spinner");
    if (btnSpinner) btnSpinner.classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData(this.form);
    if (!this.form) {
      formData.append("id", this.querySelector("[name=id]").value);
    }

    this.appendStickyBarProperties(formData);
    this.removeBlankCustomProperties(formData);

    if (this.cartUIElement) {
      formData.append(
        "sections",
        this.cartUIElement
          .getSectionsToRender()
          .map((section) => section.section)
      );
      formData.append("sections_url", window.location.pathname);
    }
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => parseCartJSON(response))
      .then((response) => {
        // If there is a status in the response, then this is an error
        // https://shopify.dev/docs/api/ajax/reference/cart#error-responses
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.description,
            message: response.message,
          });

          // [Start theme custom event] - Cart error event
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                errors: response.description,
                message: response.message,
              },
            })
          );
          const refreshCartUIEvent = new CustomEvent("cart:refresh");
          document.dispatchEvent(refreshCartUIEvent);
          // [End theme custom event] - Cart error event

          this.handleErrorMessage(response.description);
          const soldOutMessage =
            this.submitButton.querySelector(".sold-out-message");
          if (!soldOutMessage) return;
          this.submitButton.setAttribute("aria-disabled", true);
          this.submitButton.querySelector("span").classList.add("hidden");
          soldOutMessage.classList.remove("hidden");
          this.error = true;
          return;
        } else if (!this.cartUIElement) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
          });
        }
        this.error = false;

        // [Start theme custom event] - Variant addition event
        document.dispatchEvent(
          new CustomEvent("variant:add", {
            detail: {
              variant: {
                id: response.variant_id || formData.get("id"),
                quantity:
                  response.quantity || Number(formData.get("quantity") || 1),
                product_title: response.product_title || "",
                variant_title: response.variant_title || "",
                image: response.image || null,
              },
              formElement: this.form,
            },
          })
        );
        // [End theme custom event] - Variant addition event

        // [Start theme custom event] - Cart change event
        fetch(`${routes.cart_url}.js`)
          .then((response) => (response.ok ? parseCartJSON(response) : null))
          .then((parsedState) => {
            if (!parsedState) return;
            document.dispatchEvent(
              new CustomEvent("cart:change", {
                detail: {
                  cart: parsedState,
                },
              })
            );
          })
          .catch((error) => {
            console.error("Error fetching cart state:", error);
          });
        // [End theme custom event] - Cart change event

        let activeElement = document.activeElement;

        // add to cart from quick-view modal
        const quickAddModal = this.closest("quick-view-modal");
        if (quickAddModal && typeof quickAddModal?.hide === "function") {
          activeElement = quickAddModal.openedBy;
          document.body.addEventListener(
            "quick-view-closed",
            () => {
              setTimeout(() => {
                this.cartUIElement.setActiveElement(activeElement);
                this.cartUIElement.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
          return;
        }

        this.cartUIElement.setActiveElement(activeElement);
        this.cartUIElement.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
        this.handleErrorMessage(window.cartStrings?.error);
      })
      .finally(() => {
        if (this.cartUIElement?.closest("cart-notification")) {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer && cartDrawer.classList.contains("is-empty")) {
            cartDrawer.classList.remove("is-empty");
          }
        }

        if (
          this.cartUIElement?.closest("cart-drawer") &&
          this.cartUIElement?.classList.contains("is-empty")
        ) {
          this.cartUIElement.classList.remove("is-empty");
        }
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        this.submitButton.classList.remove("loading");
        if (btnSpinner) btnSpinner.classList.add("hidden");
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    const stickyBarParent = this.closest(".product-sticky-add-bar");
    const horizontalProductParent = this.closest(".product-card-horizontal");
    const errorWrapperSelector = ".product-form__error-message-wrapper";

    this.errorMessageWrapper =
      this.errorMessageWrapper ||
      this.querySelector(errorWrapperSelector) ||
      stickyBarParent?.querySelector(errorWrapperSelector) ||
      horizontalProductParent?.querySelector(errorWrapperSelector);
    if (!this.errorMessageWrapper) return;

    this.errorMessage =
      this.errorMessage ||
      this.errorMessageWrapper.querySelector(".product-form__error-message");
    if (!this.errorMessage) return;

    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = null;
    }

    this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;

      // Auto-hide error message after 3 seconds for product-card-horizontal
      if (horizontalProductParent) {
        this.errorTimeoutId = setTimeout(() => {
          this.errorMessageWrapper.toggleAttribute("hidden", true);
          this.errorMessage.textContent = "";
          this.errorTimeoutId = null;
        }, 3000);
      }
    }
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}

class ProductCard extends HTMLElement {
  // Card-specific behaviour: color-swatch / quick-option switching, featured-image swap
  // by media id, availability + price refresh, quick-add to cart and its error message.
  static mediaNavId = 0;

  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.hasColorSwatches = this.dataset?.cardColorSwatches === "true";
    this.hasQuickOptions = this.dataset?.cardQuickOption === "true";
    this.optionGroupSelector = ".js-card-option-group";

    this.addEventListener("click", this._onClick);

    this.mediaNav = this.querySelector(".product-card__media-nav");
    if (this.mediaNav) this.initMediaArrows();

    this.initMediaCursor();

    this.quickOptionTrack = this.querySelector(
      ".product-card__quick-option-track"
    );
    if (this.quickOptionTrack) {
      // Measure through a ResizeObserver rather than on hover: the arrows must
      // already be right on the first reveal, and the track resizes with the grid.
      this.quickOptionObserver = new ResizeObserver(() =>
        this.updateQuickOptionNav()
      );
      this.quickOptionObserver.observe(this.quickOptionTrack);
      // Scrolling is what flips an arrow between usable and spent, and smooth
      // scrolling fires it all the way through — coalesce to one read a frame.
      this._onQuickOptionScroll = () => {
        if (this._quickOptionRaf) return;
        this._quickOptionRaf = requestAnimationFrame(() => {
          this._quickOptionRaf = null;
          this.updateQuickOptionNav();
        });
      };
      this.quickOptionTrack.addEventListener(
        "scroll",
        this._onQuickOptionScroll,
        { passive: true }
      );
      // A font swap changes the values' width without resizing the track itself,
      // so the observer never hears about it — re-measure once fonts settle.
      document.fonts?.ready.then(() => this.updateQuickOptionNav());
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this.quickOptionObserver?.disconnect();
    if (this._onQuickOptionScroll) {
      this.quickOptionTrack?.removeEventListener(
        "scroll",
        this._onQuickOptionScroll
      );
    }
    cancelAnimationFrame(this._quickOptionRaf);
    clearTimeout(this.mediaSlideTimer);
    if (this._onMediaCursorMove) {
      this.cursorMedia?.removeEventListener(
        "pointermove",
        this._onMediaCursorMove
      );
    }
  }

  _onClick(event) {
    const nav = event.target.closest(".product-card__quick-option-nav");
    if (nav && this.quickOptionTrack) {
      const dir = nav.classList.contains("product-card__quick-option-nav--prev")
        ? -1
        : 1;
      this.quickOptionTrack.scrollBy({
        left: dir * this.quickOptionTrack.clientWidth * 0.8,
        behavior: "smooth",
      });
      return;
    }

    // Activating a label natively dispatches a click on its associated radio,
    // so handling the input alone covers both label and input clicks.
    const input = event.target.closest("input[type='radio']");
    if (input) this._onOptionInputClick(input);
  }

  // Hide the prev/next arrows when the values fit; show them only when the track
  // overflows, and grey out whichever direction has nothing left to scroll to.
  updateQuickOptionNav() {
    const track = this.quickOptionTrack;
    if (!track) return;
    const overflows = track.scrollWidth > track.clientWidth + 1;
    // A 1px slack absorbs the fractional widths a zoomed or scaled layout produces.
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    this.querySelectorAll(".product-card__quick-option-nav").forEach((btn) => {
      btn.toggleAttribute("hidden", !overflows);
      const isPrev = btn.classList.contains(
        "product-card__quick-option-nav--prev"
      );
      btn.disabled = overflows && (isPrev ? atStart : atEnd);
    });
  }

  _onOptionInputClick(input) {
    const group = input.closest(this.optionGroupSelector);
    if (!group) return;

    group
      .querySelectorAll("input")
      .forEach((el) => el.removeAttribute("checked"));
    input.setAttribute("checked", "true");
    input.checked = true;

    const matchedVariant = this.updateCardVariant(input.dataset?.variantId);

    if (group.classList.contains("js-quick-add-option")) {
      this.quickAddToCart(matchedVariant);
    }
  }

  updateCardVariant(fallbackVariantId = "") {
    const productVariants = this.parseData("[data-product-card-variants]");
    if (!productVariants) return null;

    const selectedOptions = this.getSelectedOptions();
    const matchedVariant =
      productVariants.find((variant) =>
        Object.entries(selectedOptions).every(
          ([key, value]) => variant[key] === value
        )
      ) || null;

    const variantId = matchedVariant ? matchedVariant.id : fallbackVariantId;

    this.updateFeaturedImage(matchedVariant);
    this.updateOptionsAvailability(productVariants);
    this.updateAddToCartId(variantId);
    this.updateQuickViewUrl(variantId);
    this.updateMediaLink(variantId);
    this.updatePrice(matchedVariant);
    this.resetErrorMessage();

    return matchedVariant;
  }

  getSelectedOptions() {
    const selectedOptions = {};
    this.querySelectorAll(`${this.optionGroupSelector} input:checked`).forEach(
      (input) => {
        const group = input.closest(this.optionGroupSelector);
        const optionPosition = group?.dataset?.optionPosition;
        if (optionPosition)
          selectedOptions[`option${optionPosition}`] = input.value;
      }
    );
    return selectedOptions;
  }

  updateFeaturedImage(matchedVariant) {
    const mediaId = matchedVariant?.featured_media?.id;
    if (!mediaId) return;
    const items = this.querySelectorAll(
      ".product-card__media-item[data-media-id]"
    );
    if (!items.length) return;
    let target = null;
    items.forEach((item) => {
      if (Number(item.dataset.mediaId) === Number(mediaId)) target = item;
    });
    if (!target) return;

    // On an arrow card the images are a slider, so a swatch pick slides too
    // rather than cutting — same interaction surface, same motion.
    const list = Array.from(items);
    const current = list.find(
      (item) => item.getAttribute("data-media-hidden") === "false"
    );
    if (
      this.querySelector(".product-card__media--slides") &&
      current &&
      current !== target
    ) {
      if (this.mediaSliding) return;
      const dir = list.indexOf(target) > list.indexOf(current) ? 1 : -1;
      this.slideMedia(current, target, dir);
      return;
    }

    items.forEach((item) => item.setAttribute("data-media-hidden", "true"));
    target.setAttribute("data-media-hidden", "false");
  }

  updateOptionsAvailability(productVariants) {
    const selectedOptions = this.getSelectedOptions();
    this.querySelectorAll(this.optionGroupSelector).forEach((group) => {
      const optionPosition = group.dataset?.optionPosition;
      if (!optionPosition) return;
      const optionKey = `option${optionPosition}`;
      group.querySelectorAll("input").forEach((input) => {
        const testOptions = { ...selectedOptions, [optionKey]: input.value };
        const isAvailable = productVariants.some(
          (variant) =>
            variant.available &&
            Object.entries(testOptions).every(
              ([key, value]) => variant[key] === value
            )
        );
        input.classList.toggle("disabled", !isAvailable);
      });
    });
  }

  updateAddToCartId(variantId) {
    const idInput = this.querySelector(
      '.product-card__quick-option input[name="id"]'
    );
    if (idInput && variantId) idInput.value = variantId;
  }

  updateMediaLink(variantId) {
    const link = this.querySelector(".product-card__media-link");
    if (!link || !variantId) return;
    const url = new URL(link.getAttribute("href"), window.location.origin);
    url.searchParams.set("variant", variantId);
    link.setAttribute("href", url.toString());
  }

  updateQuickViewUrl(variantId) {
    const quickViewBtn = this.querySelector(
      ".product-card__quick-view [data-product-url]"
    );
    if (!quickViewBtn || !variantId) return;
    const productUrl = quickViewBtn.dataset.productUrl;
    quickViewBtn.dataset.productUrl = productUrl.includes("variant=")
      ? productUrl.replace(/variant=\d+/, `variant=${variantId}`)
      : `${productUrl}?variant=${variantId}`;
  }

  updatePrice(matchedVariant) {
    const priceEl = this.querySelector(".product-card__information .price");
    if (!priceEl || !matchedVariant || matchedVariant.price == null) return;

    const regularEl = priceEl.querySelector(".price-item--regular");
    const oldEl = priceEl.querySelector(".price-item--old");
    const onSale = matchedVariant.compare_at_price > matchedVariant.price;

    priceEl.classList.toggle("price--on-sale", onSale);
    if (regularEl) regularEl.textContent = formatMoney(matchedVariant.price);
    if (oldEl && onSale)
      oldEl.textContent = formatMoney(matchedVariant.compare_at_price);
  }

  quickAddToCart(matchedVariant) {
    // Unavailable values are marked with the .disabled class and shown crossed-out;
    // skip the request rather than surface a raw error for a known-unavailable pick.
    if (!matchedVariant || !matchedVariant.available) return;

    const cartUIElement =
      this.closest("cart-drawer") ||
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("id", matchedVariant.id);
    formData.append("quantity", 1);
    if (cartUIElement) {
      formData.append(
        "sections",
        cartUIElement.getSectionsToRender().map((section) => section.section)
      );
      formData.append("sections_url", window.location.pathname);
    }
    config.body = formData;

    this.classList.add("product-card--adding");

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => parseCartJSON(response))
      .then((response) => {
        if (response.status) {
          this.resetErrorMessage(response.description || response.message);
          return;
        }
        this.resetErrorMessage();
        if (!cartUIElement) {
          window.location = window.routes.cart_url;
          return;
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-card",
          productVariantId: matchedVariant.id,
        });
        cartUIElement.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
        this.resetErrorMessage(window.cartStrings?.error);
      })
      .finally(() => this.classList.remove("product-card--adding"));
  }

  resetErrorMessage(message = "") {
    const errorEl = this.querySelector(".product-card__error");
    if (!errorEl) return;
    errorEl.toggleAttribute("hidden", !message);
    errorEl.textContent = message || "";
  }

  initMediaArrows() {
    const media = this.querySelector(".product-card__media");
    const items = Array.from(
      this.querySelectorAll(".product-card__media-item[data-media-id]")
    );
    if (!media || items.length < 2) {
      this.mediaNav.setAttribute("hidden", "");
      return;
    }

    // The shared .slider-nav-button keeps itself invisible until it controls
    // something — Swiper sets aria-controls on init, and this nav is driven
    // here instead, so it has to claim the media container itself.
    if (!media.id) {
      ProductCard.mediaNavId += 1;
      media.id = `ProductCardMedia-${ProductCard.mediaNavId}`;
    }
    this.mediaNav.querySelectorAll(".slider-nav-button").forEach((button) => {
      button.setAttribute("aria-controls", media.id);
    });

    media.classList.add("product-card__media--slides");

    const prev = this.mediaNav.querySelector(".slider-nav-button--prev");
    const next = this.mediaNav.querySelector(".slider-nav-button--next");
    const step = (dir) => {
      if (this.mediaSliding) return;
      const currentIndex = items.findIndex(
        (item) => item.getAttribute("data-media-hidden") === "false"
      );
      const base = currentIndex === -1 ? 0 : currentIndex;
      const from = items[base];
      const to = items[(base + dir + items.length) % items.length];
      if (from !== to) this.slideMedia(from, to, dir);
    };
    prev?.addEventListener("click", (e) => {
      e.preventDefault();
      step(-1);
    });
    next?.addEventListener("click", (e) => {
      e.preventDefault();
      step(1);
    });
  }

  // "Change with cursor": the media splits into one vertical strip per image and
  // the strip under the pointer picks the slide. The images ride a track — each
  // parked at its own index, the whole strip shifted by the current one — so the
  // change reads as a slide, the way Egoiste does it, not as a cut.
  initMediaCursor() {
    const media = this.querySelector(".product-card__media--hover-cursor");
    if (!media) return;
    const items = Array.from(
      media.querySelectorAll(".product-card__media-item[data-media-id]")
    );
    if (items.length < 2) return;

    items.forEach((item, i) => item.style.setProperty("--media-index", i));
    media.style.setProperty("--media-current", 0);
    media.classList.add("product-card__media--track");

    this.cursorMedia = media;
    this._onMediaCursorMove = (event) => {
      const bounds = media.getBoundingClientRect();
      if (!bounds.width) return;
      const ratio = (event.clientX - bounds.left) / bounds.width;
      const index = Math.min(
        items.length - 1,
        Math.max(0, Math.floor(ratio * items.length))
      );
      if (Number(media.style.getPropertyValue("--media-current")) === index) {
        return;
      }
      media.style.setProperty("--media-current", index);
      // Kept in sync so swatch switching and quick view still read the current
      // slide from the same attribute every other mode uses.
      items.forEach((item, i) =>
        item.setAttribute("data-media-hidden", i === index ? "false" : "true")
      );
    };
    media.addEventListener("pointermove", this._onMediaCursorMove);
  }

  // Cross-slide the two stacked images: the incoming one enters from the side
  // the arrow points at, the outgoing one leaves the other way. Stacked rather
  // than a real track so wrapping past the ends still costs one step, not N.
  slideMedia(from, to, dir) {
    const enterFrom = dir > 0 ? "100%" : "-100%";
    const leaveTo = dir > 0 ? "-100%" : "100%";

    this.mediaSliding = true;

    to.style.transition = "none";
    to.style.transform = `translateX(${enterFrom})`;
    to.setAttribute("data-media-hidden", "false");
    // Read the layout back so the parked offset becomes the transition's start
    // instead of being collapsed into the next style change.
    void to.offsetWidth;
    to.style.transition = "";
    to.style.transform = "translateX(0)";
    from.style.transform = `translateX(${leaveTo})`;

    const settle = () => {
      clearTimeout(this.mediaSlideTimer);
      from.removeEventListener("transitionend", settle);
      from.setAttribute("data-media-hidden", "true");
      from.style.transform = "";
      this.mediaSliding = false;
    };
    from.addEventListener("transitionend", settle, { once: true });
    // Fallback for a transition that never fires (reduced motion, hidden card).
    this.mediaSlideTimer = setTimeout(settle, 800);
  }

  parseData(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    const content = el.textContent?.trim();
    if (!content) return null;
    try {
      return JSON.parse(content);
    } catch (e) {
      return null;
    }
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.recommendationsSlider = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this.initProductRecommendations();
  }

  initProductRecommendations() {
    this.enableLoading();

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");
        if (recommendations && recommendations.innerHTML.trim().length) {
          //this.innerHTML = recommendations.innerHTML;
          this.replaceChildren(...recommendations.children);
        }

        if (recommendations.hasAttribute("data-section-has-card-slider")) {
          this.setAttribute("data-section-has-card-slider", "true");
          initSectionSliderLayout(this);
        } else if (recommendations.hasAttribute("data-is-complementary")) {
          this.initComplementarySlider();
        }

        if (recommendations.hasAttribute("data-products-count")) {
          this.setAttribute(
            "data-products-count",
            recommendations.getAttribute("data-products-count")
          );
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.disableLoading();
        this._initialized = true;
      });
  }

  initComplementarySlider() {
    const sliderEl = this.querySelector(".js-complementary-slider");
    if (!sliderEl) return;

    const prevBtn = this.querySelector(".slider-nav-button--prev");
    const nextBtn = this.querySelector(".slider-nav-button--next");
    const paginationEl = this.querySelector(".slider-pagination");

    const sliderSettings = {
      loop: false,
      slidesPerView: 1,
      spaceBetween: 8,
      speed: 800,
      watchSlideProgress: true,
      allowTouchMove: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
    };

    new Swiper(sliderEl, sliderSettings);
  }

  enableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.style.display = "flex";
  }

  disableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.remove();
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector(
        'input[name="locale_code"], input[name="country_code"]'
      ),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.elements.button.addEventListener("click", this.togglePanel.bind(this));
    this.addEventListener("keydown", this.onEscapePress.bind(this));

    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this))
    );
  }

  connectedCallback() {
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.hidePanel();
    }
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }

  showPanel() {
    this.elements.button.setAttribute("aria-expanded", "true");
    this.elements.panel.removeAttribute("hidden");
  }

  togglePanel() {
    if (this.elements.button.getAttribute("aria-expanded") === "true") {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  onEscapePress(event) {
    if (event.key === "Escape") {
      this.hidePanel();
    }
  }

  onItemClick(event) {
    event.preventDefault();
    this.elements.input.value = event.currentTarget.dataset.value;
    this.querySelector("form")?.submit();
    this.hidePanel();
  }
}

if (!customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.enableMockTimer =
      this.getAttribute("data-enable-mock-timer") === "true";

    if (this.enableMockTimer) {
      // get data for infinite mock timer
      const INFINITE_DAYS = Number(this.getAttribute("data-mock-end-day") || 2);
      const DIFF_HOURS = Number(this.getAttribute("data-mock-end-hour") || 3);
      const now = new Date();
      const futureDate = new Date(
        now.getTime() + (INFINITE_DAYS * 24 + DIFF_HOURS) * 60 * 60 * 1000
      );
      const year = futureDate.getFullYear();
      const month = String(futureDate.getMonth() + 1).padStart(2, "0");
      const day = String(futureDate.getDate()).padStart(2, "0");
      const hours = String(futureDate.getHours()).padStart(2, "0");
      const minutes = String(futureDate.getMinutes()).padStart(2, "0");
      this.endDate = `${year}-${month}-${day}`;
      this.endTime = `${hours}:${minutes}`;
    } else {
      // get data from section settings
      this.endDate = this.getAttribute("data-end-date");
      this.endTime = this.getAttribute("data-end-time");
    }
    // ----------------------------------------------------------------
    this.interval;
    this.isStarted = false;
    this.setInterval(this.endDate, this.endTime);
  }

  onInit(endDate, endTime) {
    this.completeBehavior = this.getAttribute("data-complete-behavior");
    this.parentEl = this.closest(".block-countdown-timer");
    this.countdownTimerEl = this.querySelector(".countdown-timer__timer");
    this.countdownEndEl = this.querySelector(".countdown-timer__end");
    this.daysEl = this.querySelector(".js-countdown-timer-days");
    this.hoursEl = this.querySelector(".js-countdown-timer-hours");
    this.minutesEl = this.querySelector(".js-countdown-timer-minutes");
    this.secondsEl = this.querySelector(".js-countdown-timer-seconds");
    // ----------------------------------------------------------------
    const countdownDate = new Date(`${endDate}T${endTime}`);
    const now = new Date();
    const distance = countdownDate.getTime() - now.getTime();
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    // ----------------------------------------------------------------
    if (distance < 0 && this.completeBehavior === "hide_block") {
      if (this.parentEl) {
        this.parentEl.style.display = "none";
      }
      this.style.display = "none";
      clearInterval(this.interval);
    } else if (distance < 0 && this.completeBehavior === "show_message") {
      if (this.countdownTimerEl) {
        this.countdownTimerEl.style.display = "none";
      }
      if (this.countdownEndEl) {
        this.countdownEndEl.style.display = "flex";
      }
      clearInterval(this.interval);
    } else {
      if (this.daysEl) {
        this.daysEl.textContent = this.getTwoDigitTime(days);
      }

      if (this.hoursEl) {
        this.hoursEl.textContent = this.getTwoDigitTime(hours);
      }

      if (this.minutesEl) {
        this.minutesEl.textContent = this.getTwoDigitTime(minutes);
      }

      if (this.secondsEl) {
        this.secondsEl.textContent = this.getTwoDigitTime(seconds);
      }
    }
  }

  getTwoDigitTime(time) {
    if (String(time).length === 1) {
      return "0" + time;
    }
    if (String(time) === "-1") {
      return "00";
    }
    return time;
  }

  setInterval(endDate, endTime) {
    clearInterval(this.interval);
    this.interval = setInterval(this.onInit.bind(this, endDate, endTime), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }
}

if (!customElements.get("countdown-timer")) {
  customElements.define("countdown-timer", CountdownTimer);
}
